import { styled } from "@mui/material";
import {
  ChevronRightIcon,
  Sidebar as VoltSidebar,
} from "@volt_developers/components";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ReactComponent as LogoIcon } from "assets/images/logo-sidebar.svg";
import { COMMON_TRANSLATION_KEYS } from "constants/commonTranslationsKeys";
import { LOCAL_STORAGE_KEYS } from "constants/LocalStorageItems";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { useGlobalFilter } from "hooks/useGlobalFilter";

import {
  SIDEBAR_WIDTH,
  ToggleGlobalFilterSearchStatusEvent,
} from "../../constants";
import { TEST_IDS } from "../../constants/TestIds";
import { GlobalFilterLabel } from "./GlobalFilter/GlobalFilterLabel";
import { GlobalFilterSearch } from "./GlobalFilter/GlobalFilterSearch";
import { useGlobalFilterSearch } from "./GlobalFilter/useGlobalFilterSearch";
import { sidebarConfig } from "./sidebarConfig";
import { SidebarItem } from "./SidebarItem";

interface SidebarProps {
  isHidden?: boolean;
}

export function Sidebar({ isHidden = false }: SidebarProps) {
  const { t } = useTranslation("common");
  const [isGlobalFilterSearchOpen, setIsGlobalFilterSearchOpen] = useState(
    false
  );
  const {
    handleCustomerClear,
    isGlobalFiltering,
    isLoading,
    globallyFilteredCustomer,
  } = useGlobalFilter();
  const customerSelectionInShellMenu = useFeatureFlag(
    LOCAL_STORAGE_KEYS.customerSelectionInShellMenu
  );

  const {
    isCustomerLoading,
    searchResults,
    isLoading: isGlobalFilterSearchLoading,
    isNoResults,
    hasMore,
    onSearch,
    onSelectCustomer,
    handleLoadMore,
  } = useGlobalFilterSearch();

  const handleOpenGlobalFilterSearch = () => {
    if (customerSelectionInShellMenu) {
      setIsGlobalFilterSearchOpen(true);
    }
  };

  useEffect(() => {
    if (customerSelectionInShellMenu) {
      window.addEventListener(ToggleGlobalFilterSearchStatusEvent, () =>
        setIsGlobalFilterSearchOpen((prev) => !prev)
      );
    }

    return () => {
      if (customerSelectionInShellMenu) {
        window.removeEventListener(ToggleGlobalFilterSearchStatusEvent, () =>
          setIsGlobalFilterSearchOpen((prev) => !prev)
        );
      }
    };
  }, [customerSelectionInShellMenu]);

  return (
    <VoltSidebar.Wrapper
      sx={{
        width: SIDEBAR_WIDTH,
        gridTemplateRows: "0fr auto",
        visibility: isHidden ? "hidden" : "visible",
      }}
    >
      <VoltSidebar.Header sx={{ width: SIDEBAR_WIDTH, height: "auto" }}>
        <VoltSidebar.GlobalFilter
          label={t(COMMON_TRANSLATION_KEYS.viewing)}
          isActive={isGlobalFiltering}
          expandIcon={customerSelectionInShellMenu && <ChevronRightIcon />}
          customerLabel={
            <GlobalFilterLabel
              isLoading={isLoading || isCustomerLoading}
              customerName={globallyFilteredCustomer?.name}
            />
          }
          data-testid={TEST_IDS.sidebarGlobalFilter}
          onClick={handleOpenGlobalFilterSearch}
          onRemoveClick={handleCustomerClear}
        />
      </VoltSidebar.Header>
      <VoltSidebar.Menu>
        {sidebarConfig.map((sidebarItem) => (
          <SidebarItem {...sidebarItem} key={sidebarItem.id} />
        ))}
      </VoltSidebar.Menu>
      <VoltSidebar.Footer>
        <LogoWrapper>
          <LogoIcon width={56} height={56} />
        </LogoWrapper>
      </VoltSidebar.Footer>
      {isGlobalFilterSearchOpen && (
        <GlobalFilterSearch
          searchResults={searchResults}
          isLoading={isGlobalFilterSearchLoading}
          isNoResults={isNoResults}
          hasMore={hasMore}
          handleLoadMore={handleLoadMore}
          onCloseFilterSearch={setIsGlobalFilterSearchOpen}
          onSearch={onSearch}
          onSelectCustomer={onSelectCustomer}
        />
      )}
    </VoltSidebar.Wrapper>
  );
}

const LogoWrapper = styled("div")(({ theme }) => ({
  color: theme.palette.text.dlow,
  display: "flex",
  alignItems: "center",
  margin: theme.spacing(1.75, 0),
}));
